// @flow
import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import { defineMessages, FormattedMessage, intlShape } from 'react-intl';
import classNames from 'classnames/bind';
import { FormValidator } from '../../../../utils/form/index';
import SuccessErrorModal from '../../../../elements/SuccessErrorModal/SuccessErrorModal';

import commonStyle from '../../PreferencesViewCommon.scss';
import boxStyle from '../../../../assets/styles/box.scss';
import buttonStyle from '../../../../assets/styles/button.scss';
import inputStyle from '../../../../assets/styles/input.scss';
import formStyle from '../../../../assets/styles/form.scss';

import type { HTTPError } from '../../../../error';
import type { FormValidationResult } from '../../../../utils/form';

type Props = {
  editPassword: (oldPassword: string, password: string) => void;
  editSuccessful?: boolean;
  isPuttingPassword?: boolean;
  putPasswordError?: HTTPError;
  resetAlertState: () => void;
};

type State = {
  oldPassword: {
    value: string;
    errors?: string[];
  };
  newPassword: {
    value: string;
    errors?: string[];
  };
  repeatNewPassword: {
    value: string;
    errors?: string[];
  };
  modalVisible: boolean;
};

const messages = defineMessages({
  title: {
    id: 'preferences.password.document.title',
  },
  description: {
    id: 'preferences.password.document.description',
  }
});

export default class Password extends Component<Props, State> {
  formValidatorPasswords: FormValidator;

  constructor(props: Props) {
    super(props);
    this.formValidatorPasswords = new FormValidator({
      oldPassword: {
        required: 'required',
        min: (value: string) => value.length < 8,
      },
      newPassword: {
        required: 'required',
        min: (value: string) => value.length < 8,
      },
      repeatNewPassword: {
        required: (values: string[]) => !values[0],
        notSame: (values: string[]) => values[0] !== values[1]
      }
    });
    this.state = {
      oldPassword: { value: '' },
      newPassword: { value: '' },
      repeatNewPassword: { value: '' },
      modalVisible: false,
    }
  }

  componentDidUpdate(prevProps: Props) {
    const { editSuccessful, putPasswordError } = this.props;
    if (editSuccessful && (prevProps.editSuccessful !== editSuccessful)) {
      this.setState({
        oldPassword: { value: '' },
        newPassword: { value: '' },
        repeatNewPassword: { value: '' },
        modalVisible: true,
      })
    }

    if (putPasswordError && (prevProps.putPasswordError !== putPasswordError)) {
      if (putPasswordError.code === 401 && putPasswordError.data && putPasswordError.data.name === 'AuthenticationFailedError') {
        this.setState({
          oldPassword: {
            ...this.state.oldPassword,
            errors: (this.state.oldPassword.errors || []).concat('wrongCredentials'),
          },
        })
      }
      if (putPasswordError.code === 400 && putPasswordError.data && putPasswordError.data.name === 'ValidationError') {
        this.setState({
          oldPassword: {
            ...this.state.oldPassword,
            errors: (this.state.oldPassword.errors || []).concat('mustBeNotSame'),
          },
          newPassword: {
            ...this.state.newPassword,
            errors: (this.state.newPassword.errors || []).concat('mustBeNotSame'),
          },
          repeatNewPassword: {
            ...this.state.repeatNewPassword,
            errors: (this.state.repeatNewPassword.errors || []).concat('mustBeNotSame'),
          },
        })
      }
    }
  }

  componentWillUnmount() {
    const { resetAlertState } = this.props;
    resetAlertState();
  }

  handleChangePasswords(e: Event) {
    if (e.target instanceof HTMLInputElement) {
      const { name, value } = e.target;
      this.setState({ [name]: { value } });
    }
  }

  handleBlurPasswords(e: Event) {
    if (e.target instanceof HTMLInputElement) {
      const { name, value } = e.target;
      const v = name === 'repeatNewPassword' ? [value, this.state.newPassword.value] : value;

      const errors = this.formValidatorPasswords.validateField(name, v);
      if (errors.length && (errors[0] === 'notSame' || errors[0] === 'min')) {
        this.setState({ [name]: { value, errors } });
      } else {
        this.setState({ [name]: { value } });
      }
    }
  }

  handleFocusPasswords(e: Event) {
    if (e.target instanceof HTMLInputElement) {
      const { name, value } = e.target;
      this.setState({ [name]: { value } });
    }
  }

  handleSubmitPassword(e: Event) {
    e.preventDefault();
    const { editPassword } = this.props;
    const oldPasswordValue = this.state.oldPassword.value;
    const newPasswordValue = this.state.newPassword.value;
    const repeatNewPasswordValue = this.state.repeatNewPassword.value;
    const errors = this.formValidatorPasswords.validateForm({
      oldPassword: oldPasswordValue,
      newPassword: newPasswordValue,
      repeatNewPassword: [repeatNewPasswordValue, newPasswordValue],
    });
    if (errors.length) {
      errors.forEach((error: FormValidationResult) =>
        this.setState({ [error.field]: { ...this.state[error.field], errors: error.errors, }, }));
    } else {
      editPassword(oldPasswordValue, newPasswordValue);
    }
  }

  handleCloseModal() {
    this.setState({ modalVisible: false });
  }

  render() {
    const { isPuttingPassword, putPasswordError } = this.props;
    const { oldPassword, newPassword, repeatNewPassword, modalVisible } = this.state;
    const oldPasswordError = oldPassword.errors && FormValidator.buildErrorLabel(oldPassword.errors, {
      required: 'preferences.password.oldPassword.error.required',
      min: 'preferences.password.oldPassword.error.min',
    });
    const newPasswordError = newPassword.errors && FormValidator.buildErrorLabel(newPassword.errors, {
      required: 'preferences.password.newPassword.error.required',
      min: 'preferences.password.newPassword.error.min',
    });
    const repeatNewPasswordError = repeatNewPassword.errors && FormValidator.buildErrorLabel(repeatNewPassword.errors, {
      required: 'preferences.password.repeatNewPassword.error.required',
      min: 'preferences.password.repeatNewPassword.error.min',
      notSame: 'preferences.password.repeatNewPassword.error.notSame',
    });

    let error;
    if (putPasswordError) {
      if (putPasswordError.code === 401 && putPasswordError.data) {
        if (putPasswordError.data.name === 'AuthenticationFailedError') {
          error = <FormattedMessage id="preferences.password.updatePassworde.error.authentication">{
            txt => (<p className={classNames(commonStyle.text, commonStyle.red, commonStyle.lowerError)}>{txt}</p>)
          }</FormattedMessage>;
        }
      }
      if (putPasswordError.code === 400 && putPasswordError.data) {
        if (putPasswordError.data.name === 'ValidationError') {
          error = <FormattedMessage id="preferences.password.updatePassword.error.validation">{
            txt => (<p className={classNames(commonStyle.text, commonStyle.red, commonStyle.lowerError)}>{txt}</p>)
          }</FormattedMessage>;
        }
      }
    }

    return <Fragment>
      <Helmet key="helmet">
        <title>{this.context.intl.formatMessage(messages.title)}</title>
        <meta name="description" content={this.context.intl.formatMessage(messages.description)}/>
      </Helmet>
      <section className={classNames(boxStyle.box, commonStyle.contentWrap)}>
        <FormattedMessage id="preferences.password.title">{
          txt => <h2 className={classNames(boxStyle.boxTitle, boxStyle.smallSpace)}>{txt}</h2>
        }</FormattedMessage>
        <FormattedMessage id="preferences.password.subtitle">{
          txt => <p className={boxStyle.subtitle}>{txt}</p>
        }</FormattedMessage>
        <form className={classNames(commonStyle.inputs, formStyle.formWrap)}
              onSubmit={this.handleSubmitPassword.bind(this)}>
          <section className={classNames(formStyle.row, formStyle.inputRow)}>
            <FormattedMessage id="preferences.password.oldPassword.label">{
              txt => <label className={formStyle.label}>{txt}</label>
            }</FormattedMessage>
            <input
              className={classNames(inputStyle.input, { [inputStyle.red]: oldPassword.errors && oldPassword.errors.length },
                { [inputStyle.lightBlue]: !!oldPassword.value })}
              disabled={isPuttingPassword}
              name="oldPassword"
              onChange={this.handleChangePasswords.bind(this)}
              onBlur={this.handleBlurPasswords.bind(this)}
              onFocus={this.handleFocusPasswords.bind(this)}
              type="password"
              value={oldPassword.value}/>
            {oldPasswordError}
          </section>
          <section className={classNames(formStyle.row, formStyle.inputRow)}>
            <FormattedMessage id="preferences.password.newPassword.label">{
              txt => <label className={formStyle.label}>{txt}</label>
            }</FormattedMessage>
            <input
              className={classNames(inputStyle.input, { [inputStyle.red]: newPassword.errors && newPassword.errors.length },
                { [inputStyle.lightBlue]: !!newPassword.value })}
              disabled={isPuttingPassword}
              name="newPassword"
              onChange={this.handleChangePasswords.bind(this)}
              onBlur={this.handleBlurPasswords.bind(this)}
              onFocus={this.handleFocusPasswords.bind(this)}
              type="password"
              value={newPassword.value}/>
            {newPasswordError}
          </section>
          <section className={classNames(formStyle.row, formStyle.inputRow)}>
            <FormattedMessage id="preferences.password.repeatNewPassword.label">{
              txt => <label className={formStyle.label}>{txt}</label>
            }</FormattedMessage>
            <input
              className={classNames(inputStyle.input, { [inputStyle.red]: repeatNewPassword.errors && repeatNewPassword.errors.length },
                { [inputStyle.lightBlue]: !!repeatNewPassword.value })}
              disabled={isPuttingPassword}
              name="repeatNewPassword"
              onChange={this.handleChangePasswords.bind(this)}
              onBlur={this.handleBlurPasswords.bind(this)}
              onFocus={this.handleFocusPasswords.bind(this)}
              type="password"
              value={repeatNewPassword.value}/>
            {repeatNewPasswordError}
          </section>
          {error}
          <section className={classNames(formStyle.row, formStyle.inputRow, commonStyle.button)}>
            <FormattedMessage id="preferences.password.updatePassword">{
              txt => <button type="submit"
                             className={classNames(buttonStyle.btn, buttonStyle.lightBlue)}
                             disabled={isPuttingPassword}
                             onClick={this.handleSubmitPassword.bind(this)}
              >{txt}</button>
            }</FormattedMessage>
          </section>
        </form>
      </section>
      <SuccessErrorModal isOpen={modalVisible}
                         handleCloseModal={this.handleCloseModal.bind(this)}
                         titleInt="preferences.modal.successfulUpdate.title"
                         textInt="preferences.modal.successfulUpdate.text"
                         color="lightBlue"
                         success={true}
      />
    </Fragment>
  }
}

Password.propTypes = {
  editPassword: PropTypes.func.isRequired,
  editSuccessful: PropTypes.bool,
  isPuttingPassword: PropTypes.bool,
  putPasswordError: PropTypes.object,
  resetAlertState: PropTypes.func.isRequired,
};

Password.contextTypes = {
  intl: intlShape,
};



// WEBPACK FOOTER //
// ./src/modules/preferences/password/components/Password.jsx